import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { FileText } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import CompactFileDropzone from '../tenant/CompactFileDropzone';
import ClosingDocumentSheet from './ClosingDocumentSheet';
import { CLOSING_DOCUMENT_ACCEPT, CLOSING_DOCUMENT_HINT, closingFileProblem, type ClosingReviewState } from './closing-model';
import ClosingReviewStatusCard, { ClosingReviewOverlay, ClosingReviewStatusRow } from './ClosingReviewStatusCard';
import {
  CLOSING_DOCUMENT_PANEL,
  CLOSING_STEP_INSTRUCTIONS_NEUTRAL,
  CLOSING_UPLOAD_INSTRUCTIONS_CARD,
  CLOSING_UPLOAD_PAIR_GRID,
  CLOSING_UPLOAD_PREVIEW_SHELL,
  CLOSING_UPLOAD_SECONDARY_ROW_MIN,
} from './closing-styles';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

type PreviewKind = 'image' | 'pdf' | 'none';

function previewKindFromName(fileName: string, mimeType?: string): PreviewKind {
  const type = (mimeType ?? '').toLowerCase();
  const title = fileName.toLowerCase();
  if (type === 'application/pdf' || title.endsWith('.pdf')) return 'pdf';
  if (type.startsWith('image/') && !type.includes('heic') && !type.includes('heif') && !type.includes('tiff')) {
    return 'image';
  }
  const extension = title.split('.').pop() ?? '';
  if (['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp'].includes(extension)) return 'image';
  return 'none';
}

function previewKind(file: File): PreviewKind {
  return previewKindFromName(file.name, file.type);
}

function downloadLocalFile(file: File): void {
  const url = URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = url;
  link.download = file.name || 'document';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function pageAspect(page: { view?: number[]; width?: number; height?: number; originalWidth?: number; originalHeight?: number }): number | null {
  const box = page.view;
  const width = page.originalWidth ?? page.width ?? (box ? box[2] - box[0] : 0);
  const height = page.originalHeight ?? page.height ?? (box ? box[3] - box[1] : 0);
  if (width <= 0 || height <= 0) return null;
  return width / height;
}

function DocumentPreviewFrame({
  kind,
  url,
  loading,
  error,
  intrinsic = false,
}: {
  kind: PreviewKind;
  url: string | null;
  loading: boolean;
  error: string | null;
  /** Size the frame to the image, or to the first PDF page, and scroll further pages. */
  intrinsic?: boolean;
}) {
  const frameRef = useRef<HTMLDivElement>(null);
  const [frameWidth, setFrameWidth] = useState(160);
  const [aspectRatio, setAspectRatio] = useState<number | null>(null);
  const [pdfPageCount, setPdfPageCount] = useState(1);

  useLayoutEffect(() => {
    const node = frameRef.current;
    if (!node) return;
    const measure = () => setFrameWidth(Math.max(80, Math.floor(node.clientWidth)));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setAspectRatio(null);
    setPdfPageCount(1);
  }, [url]);

  const lockPdfAspect = intrinsic && kind === 'pdf';

  return (
    <div
      ref={frameRef}
      className={intrinsic ? 'relative w-full min-w-0 overflow-hidden bg-white dark:bg-white/5' : 'absolute inset-0 overflow-hidden bg-white dark:bg-white/5'}
      style={lockPdfAspect ? { aspectRatio: aspectRatio ? String(aspectRatio) : '3 / 4' } : undefined}
    >
      {loading ? (
        <p className="absolute inset-0 flex items-center justify-center px-3 text-center text-sm text-gray-500 dark:text-gray-400">
          Loading preview…
        </p>
      ) : null}
      {error ? (
        <p className="absolute inset-0 flex items-center justify-center px-3 text-center text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      ) : null}
      {url && kind === 'image' ? (
        <img
          src={url}
          alt=""
          className="block h-auto w-full max-w-full"
          onLoad={(event) => {
            const image = event.currentTarget;
            if (image.naturalWidth > 0 && image.naturalHeight > 0) {
              setAspectRatio(image.naturalWidth / image.naturalHeight);
            }
          }}
        />
      ) : null}
      {url && kind === 'pdf' ? (
        <div className="absolute inset-0 overflow-y-auto">
          <Document
            file={url}
            loading={<p className="px-3 py-8 text-center text-sm text-gray-500 dark:text-gray-400">Loading…</p>}
            onLoadSuccess={({ numPages }) => setPdfPageCount(numPages)}
          >
            {Array.from({ length: pdfPageCount }, (_, index) => (
              <Page
                key={index + 1}
                pageNumber={index + 1}
                width={frameWidth}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                onLoadSuccess={(page) => {
                  if (index !== 0) return;
                  const ratio = pageAspect(page);
                  if (ratio) setAspectRatio(ratio);
                }}
                className={index > 0 ? 'border-t border-gray-200' : undefined}
              />
            ))}
          </Document>
        </div>
      ) : null}
      {url && kind === 'none' ? (
        <p className="absolute inset-0 flex items-center justify-center px-3 text-center text-sm text-gray-500 dark:text-gray-400">
          This file can&apos;t be previewed here.
        </p>
      ) : null}
    </div>
  );
}

function LocalDocumentPreview({ file, intrinsic = false }: { file: File; intrinsic?: boolean }) {
  const [url, setUrl] = useState<string | null>(null);
  const kind = previewKind(file);

  useEffect(() => {
    const next = URL.createObjectURL(file);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [file]);

  return <DocumentPreviewFrame kind={kind} url={url} loading={!url} error={null} intrinsic={intrinsic} />;
}

export function ClosingSavedDocumentPreview({
  documentId,
  fileName,
  getSavedDocumentUrl,
  intrinsic = false,
}: {
  documentId: string;
  fileName: string;
  getSavedDocumentUrl: (documentId: string) => Promise<string>;
  intrinsic?: boolean;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const kind = previewKindFromName(fileName);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setUrl(null);
    void getSavedDocumentUrl(documentId)
      .then((next) => {
        if (!cancelled) setUrl(next);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Unable to load preview.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [documentId, getSavedDocumentUrl]);

  return <DocumentPreviewFrame kind={kind} url={url} loading={loading} error={error} intrinsic={intrinsic} />;
}

function ClosingUploadedFileCell({
  fileName,
  file,
  canOpenSaved,
  disabled,
  onClear,
  onDeleteSaved,
  onReplaceSaved,
  onOpenSaved,
  onDownloadLocal,
  onReplaceFile,
}: {
  fileName: string;
  file: File | null;
  canOpenSaved: boolean;
  disabled: boolean;
  onClear: () => void;
  onDeleteSaved?: () => void;
  /** When set, Replace clears the saved file instead of picking a new one. */
  onReplaceSaved?: () => void;
  onOpenSaved: () => void;
  onDownloadLocal: () => void;
  onReplaceFile: (file: File) => void;
}) {
  const [replaceProblem, setReplaceProblem] = useState<string | null>(null);

  function takeReplacement(next: File | undefined) {
    if (!next || disabled) return;
    const problem = closingFileProblem(next);
    if (problem) {
      setReplaceProblem(problem);
      return;
    }
    setReplaceProblem(null);
    onReplaceFile(next);
  }

  return (
    <div
      className={`flex ${CLOSING_UPLOAD_SECONDARY_ROW_MIN} min-w-0 flex-wrap items-center gap-x-3 gap-y-1 overflow-hidden rounded-xl border border-dashed border-gray-400 bg-white/70 px-3 py-2 dark:border-white/20 dark:bg-gray-700`}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <FileText size={16} strokeWidth={1.75} className="shrink-0 text-gray-500 dark:text-gray-400" aria-hidden />
        {file ? (
          <button
            type="button"
            className="max-w-full truncate text-sm font-medium text-gray-900 underline underline-offset-2 dark:text-white"
            onClick={onDownloadLocal}
          >
            {fileName}
          </button>
        ) : canOpenSaved ? (
          <button
            type="button"
            className="max-w-full truncate text-sm font-medium text-gray-900 underline underline-offset-2 dark:text-white"
            onClick={() => void onOpenSaved()}
          >
            {fileName}
          </button>
        ) : (
          <p className="max-w-full truncate text-sm font-medium text-gray-900 dark:text-white">{fileName}</p>
        )}
      </div>
      <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
        {onReplaceSaved ? (
          <button
            type="button"
            className="rounded-lg border border-gray-300 bg-white px-2.5 py-1 text-xs font-semibold text-gray-900 hover:bg-gray-50 disabled:opacity-50 dark:border-white/15 dark:bg-gray-900 dark:text-white dark:hover:bg-gray-800"
            onClick={onReplaceSaved}
            disabled={disabled}
          >
            Replace file
          </button>
        ) : (
          <label
            className={`inline-flex cursor-pointer rounded-lg border border-gray-300 bg-white px-2.5 py-1 text-xs font-semibold text-gray-900 hover:bg-gray-50 dark:border-white/15 dark:bg-gray-900 dark:text-white dark:hover:bg-gray-800 ${
              disabled ? 'pointer-events-none opacity-50' : ''
            }`}
          >
            <input
              className="sr-only"
              type="file"
              accept={CLOSING_DOCUMENT_ACCEPT}
              disabled={disabled}
              onChange={(event) => {
                takeReplacement(event.target.files?.[0]);
                event.target.value = '';
              }}
            />
            Replace file
          </label>
        )}
        {file ? (
          <button
            type="button"
            className="rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-800 hover:bg-rose-100 disabled:opacity-50 dark:border-rose-900/50 dark:bg-rose-950/50 dark:text-rose-200 dark:hover:bg-rose-950"
            onClick={onClear}
            disabled={disabled}
          >
            Cancel
          </button>
        ) : null}
        {!file && onDeleteSaved ? (
          <button
            type="button"
            className="rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-800 hover:bg-rose-100 disabled:opacity-50 dark:border-rose-900/50 dark:bg-rose-950/50 dark:text-rose-200 dark:hover:bg-rose-950"
            onClick={onDeleteSaved}
            disabled={disabled}
          >
            Delete file
          </button>
        ) : null}
      </div>
      {replaceProblem ? (
        <p className="basis-full text-xs text-rose-700 dark:text-rose-300">{replaceProblem}</p>
      ) : null}
    </div>
  );
}

export default function ClosingDocumentUpload({
  note,
  fileName,
  file,
  savedDocumentId,
  getSavedDocumentUrl,
  onFile,
  onClear,
  onDeleteSaved,
  onReplaceSaved,
  disabled = false,
  reviewStatus,
  instructionsExtra,
  instructionsTone = 'neutral',
  emptyPreview = 'Your file will appear here.',
  previewSheet = false,
  sheetPanel = false,
}: {
  note: string;
  /** Additional instruction copy below the main note (utilities-specific steps, etc.). */
  instructionsExtra?: ReactNode;
  /** Sky panel for Utilities Kingston; neutral gray for other closing uploads. */
  instructionsTone?: 'neutral' | 'utilities';
  fileName: string | null;
  file: File | null;
  savedDocumentId?: string | null;
  getSavedDocumentUrl?: (documentId: string) => Promise<string>;
  onFile: (file: File) => void;
  onClear: () => void;
  onDeleteSaved?: () => void;
  /** When set, Replace clears the saved file instead of picking a new one. */
  onReplaceSaved?: () => void;
  disabled?: boolean;
  reviewStatus?: { review: ClosingReviewState; hasSubmission: boolean };
  /** Shown in the preview until a file is present. */
  emptyPreview?: string;
  /** Paper sheet with the lease-style shadow, kept around the real file. */
  previewSheet?: boolean;
  /** Steps and the document share one gray field, as on Sign the Lease. */
  sheetPanel?: boolean;
}) {
  const canOpenSaved =
    Boolean(savedDocumentId && getSavedDocumentUrl && fileName && !file);

  async function openSavedFile() {
    if (!savedDocumentId || !getSavedDocumentUrl) return;
    const url = await getSavedDocumentUrl(savedDocumentId);
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  const hasSubmission = Boolean(fileName || file);
  const displayFileName = fileName ?? file?.name ?? null;

  const reviewSlot =
    hasSubmission && reviewStatus ? (
      <ClosingReviewStatusCard
        review={reviewStatus.review}
        hasSubmission={reviewStatus.hasSubmission}
        fillCell
      />
    ) : (
      <ClosingReviewStatusRow
        dashed
        tone="incomplete"
        label="Incomplete"
        detail="Upload a file to submit it for review."
      />
    );

  const instructionsClass =
    instructionsTone === 'utilities' ? CLOSING_UPLOAD_INSTRUCTIONS_CARD : CLOSING_STEP_INSTRUCTIONS_NEUTRAL;
  const noteClass =
    instructionsTone === 'utilities'
      ? 'text-sm leading-relaxed text-sky-950 dark:text-sky-200'
      : 'text-sm leading-relaxed text-gray-800 dark:text-gray-200';

  const documentSheet = (
    <ClosingDocumentSheet
      filled={hasSubmission}
      intrinsic={previewSheet}
      overlay={
        hasSubmission && reviewStatus ? (
          <ClosingReviewOverlay review={reviewStatus.review} hasSubmission={reviewStatus.hasSubmission} />
        ) : undefined
      }
    >
      {file ? (
        <LocalDocumentPreview file={file} intrinsic />
      ) : savedDocumentId && getSavedDocumentUrl && fileName ? (
        <ClosingSavedDocumentPreview
          documentId={savedDocumentId}
          fileName={fileName}
          getSavedDocumentUrl={getSavedDocumentUrl}
          intrinsic
        />
      ) : (
        emptyPreview
      )}
    </ClosingDocumentSheet>
  );

  const instructionBody = (
    <>
      {note.trim() ? <p className={noteClass}>{note}</p> : null}
      {instructionsExtra ? (
        <div className={`flex min-h-0 flex-1 flex-col ${note.trim() ? 'mt-3' : ''}`}>{instructionsExtra}</div>
      ) : null}
    </>
  );
  const instructions = sheetPanel ? (
    <div>{instructionBody}</div>
  ) : (
    <aside className={instructionsClass} role="note">
      {instructionBody}
    </aside>
  );

  const fileRow = hasSubmission && displayFileName ? (
    <ClosingUploadedFileCell
      fileName={displayFileName}
      file={file}
      canOpenSaved={canOpenSaved}
      disabled={disabled}
      onClear={onClear}
      onDeleteSaved={onDeleteSaved}
      onReplaceSaved={onReplaceSaved}
      onOpenSaved={() => void openSavedFile()}
      onDownloadLocal={() => downloadLocalFile(file!)}
      onReplaceFile={onFile}
    />
  ) : (
    <CompactFileDropzone
      compact
      className={`mt-0 ${CLOSING_UPLOAD_SECONDARY_ROW_MIN}`}
      disabled={disabled}
      accept={CLOSING_DOCUMENT_ACCEPT}
      hint={CLOSING_DOCUMENT_HINT}
      check={closingFileProblem}
      onFile={onFile}
    />
  );

  if (previewSheet) {
    const columns = (
      <div className="flex flex-col gap-4 sm:flex-row sm:items-stretch sm:justify-between sm:gap-6">
        <div className="flex min-w-0 flex-1 flex-col">
          {instructions}
          <div className="mt-4 sm:mt-auto sm:pt-4">{fileRow}</div>
        </div>
        <div className="flex w-full max-w-[16rem] shrink-0 justify-center self-start sm:w-[16rem]">
          {documentSheet}
        </div>
      </div>
    );
    if (sheetPanel) return <div className={CLOSING_DOCUMENT_PANEL}>{columns}</div>;
    return <div className="mt-3">{columns}</div>;
  }

  return (
    <div className={CLOSING_UPLOAD_PAIR_GRID}>
      {instructions}

      {file ? (
        <div className={CLOSING_UPLOAD_PREVIEW_SHELL}>
          <LocalDocumentPreview file={file} />
        </div>
      ) : savedDocumentId && getSavedDocumentUrl && fileName ? (
        <div className={CLOSING_UPLOAD_PREVIEW_SHELL}>
          <ClosingSavedDocumentPreview
            documentId={savedDocumentId}
            fileName={fileName}
            getSavedDocumentUrl={getSavedDocumentUrl}
          />
        </div>
      ) : (
        <div className="flex items-start justify-center">
          <ClosingDocumentSheet>{emptyPreview}</ClosingDocumentSheet>
        </div>
      )}

      {fileRow}

      <div className={`flex h-full w-full ${CLOSING_UPLOAD_SECONDARY_ROW_MIN} flex-col`}>{reviewSlot}</div>
    </div>
  );
}
